export { getObjectDifferences } from '@sosocio/tools/object-differences';

function toType<T extends Record<string, any>>(object: T): string {
	// Get fine type (object, array, function, null, error, date ...)
	return ({})
		.toString
		.call(object)
		.match(/([a-z]+)(:?\])/i)?.[1] as string;
}

function isDeepObject<T extends object>(object: T): boolean {
	return toType(object) === 'Object';
}

export function deepAssign<T extends Record<string, any>, U>(target: T, source: U): T & U;
export function deepAssign<T extends Record<string, any>, U, V>(target: T, source1: U, source2: V): T & U & V;
export function deepAssign<T extends Record<string, any>, U, V, W>(target: T, source1: U, source2: V, source3: W): T & U & V & W;
export function deepAssign(
	target: Record<string, any>,
	...sources: any[]
): any {
	sources.forEach((source) => {
		if (!isDeepObject(source) || !isDeepObject(target)) {
			return;
		}

		// Copy source's own properties into target's own properties
		function copyProperty(property: string) {
			const descriptor = Object.getOwnPropertyDescriptor(
				source,
				property,
			);

			// default: omit non-enumerable properties
			if (descriptor?.enumerable) {
				// Copy in-depth first
				if (
					isDeepObject(source[property])
					&& isDeepObject(target[property as keyof typeof target])
				) {
					descriptor.value = deepAssign(
						target[property as keyof typeof target],
						source[property],
					);
				}

				Object.defineProperty(
					target,
					property,
					descriptor,
				); // shallow copy descriptor
			}
		}

		// Copy string-keyed properties
		Object
			.getOwnPropertyNames(source)
			.forEach(copyProperty);
	});

	return target;
}
